import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, Clock, Users, Sparkles, ChevronRight, Shield, Zap } from 'lucide-react';

const OfferCard = ({ offer, variant = 'featured' }) => {
  const [isHovered, setIsHovered] = useState(false);
   const badgeColor = {
    HOT: 'bg-gradient-to-r from-red-500 to-orange-500',
    NEW: 'bg-gradient-to-r from-green-500 to-emerald-500',
    LIMITED: 'bg-gradient-to-r from-yellow-500 to-amber-500',
    TRIAL: 'bg-gradient-to-r from-blue-500 to-cyan-500',
    EXCLUSIVE: 'bg-gradient-to-r from-purple-500 to-pink-500'
  }[offer.badgeType || 'NEW'];

  if (variant === 'small') {
    return (
      <Link to={offer.route} className="flex-shrink-0 w-36 sm:w-44">
        <motion.div
          className="relative w-full aspect-square rounded-2xl overflow-hidden mb-3"
          whileHover={{ y: -8 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          <motion.div
            className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-pink-500/20"
            animate={{ opacity: isHovered ? 1 : 0 }}
          />
          <motion.img
            src={offer.image}
            alt={offer.title}
            className="w-full h-full object-cover"
            whileHover={{ scale: 1.1 }}
            transition={{ duration: 0.4 }}
          />
          
          {/* Shimmer overlay */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
            initial={{ x: '-100%' }}
            whileHover={{ x: '100%' }}
            transition={{ duration: 0.8 }}
          />
          
          {offer.badge && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.1 }}
              className={`absolute top-2 left-2 px-2 py-1 rounded-full text-[10px] font-bold text-white ${
                offer.badgeType === 'HOT' ? 'bg-gradient-to-r from-red-500 to-orange-500' :
                offer.badgeType === 'NEW' ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                'bg-gradient-to-r from-blue-500 to-cyan-500'
              }`}
            >
              {offer.badge}
            </motion.div>
          )}
        </motion.div>
        
        <div className="space-y-1">
          <h4 className="text-sm sm:text-[15px] font-bold truncate text-gray-200 leading-tight">
            {offer.title}
          </h4>
          <p className="text-xs sm:text-[13px] text-gray-400 truncate leading-snug">
            {offer.subtitle}
          </p>
          
          {/* Quick stats */}
          <div className="flex items-center gap-3 text-xs text-gray-500">
            {offer.duration && (
              <motion.div 
                className="flex items-center gap-1"
                whileHover={{ scale: 1.05 }}
              >
                <Clock className="w-3 h-3" />
                <span className="text-[11px]">{offer.duration}</span>
              </motion.div>
            )}
            {offer.users && (
              <motion.div 
                className="flex items-center gap-1"
                whileHover={{ scale: 1.05 }}
              >
                <Users className="w-3 h-3" />
                <span className="text-[11px]">{offer.users}</span>
              </motion.div>
            )}
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'medium') {
    return (
      <Link to={offer.route} className="block rounded-2xl overflow-hidden mb-3 bg-gradient-to-br from-gray-900/80 to-black/80 border border-gray-800/40 shadow-lg p-4">
        <div className="flex items-center gap-4">
          <img src={offer.image} alt={offer.title} className="w-28 h-28 rounded-xl object-cover flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-bold text-white truncate">{offer.title}</h4>
            <p className="text-xs text-gray-400 truncate mt-1">{offer.subtitle}</p>

            <div className="mt-3 flex items-center gap-2">
              {offer.duration && <div className="text-[11px] text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" />{offer.duration}</div>}
              {offer.users && <div className="text-[11px] text-gray-400 flex items-center gap-1"><Users className="w-3 h-3" />{offer.users}</div>}
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'large') {
    return (
      <Link to={offer.route} className="block rounded-3xl overflow-hidden mb-4 bg-gradient-to-br from-gray-900/90 to-black/90 border border-gray-800/50 shadow-2xl p-6">
        <div className="flex flex-col lg:flex-row gap-6 items-start">
          <div className="w-full lg:w-1/2 rounded-2xl overflow-hidden">
            <img src={offer.image} alt={offer.title} className="w-full h-56 lg:h-64 object-cover rounded-2xl" />
          </div>

          <div className="flex-1">
            {offer.badge && <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold text-white ${badgeColor}`}> <Zap className="w-3 h-3" />{offer.badge}</div>}
            <h3 className="mt-4 lg:mt-2 text-xl lg:text-2xl font-bold text-white leading-tight">{offer.title}</h3>
            <p className="mt-2 text-sm text-gray-300 leading-relaxed">{offer.subtitle}</p>

            <div className="mt-4 flex flex-wrap gap-3">
              {offer.duration && <div className="flex items-center gap-1.5 text-xs text-gray-400 bg-gray-800/50 px-2.5 py-1.5 rounded-lg"><Clock className="w-3.5 h-3.5" /><span className="text-[11px]">{offer.duration}</span></div>}
              {offer.users && <div className="flex items-center gap-1.5 text-xs text-gray-400 bg-gray-800/50 px-2.5 py-1.5 rounded-lg"><Users className="w-3.5 h-3.5" /><span className="text-[11px]">{offer.users}</span></div>}
            </div>

            <div className="mt-6 w-full lg:w-auto">
              <div className={`inline-block py-3 px-6 rounded-2xl font-bold text-sm text-white ${offer.color || 'bg-gradient-to-r from-indigo-500 to-purple-600'}`}>{offer.ctaLabel}</div>
            </div>
          </div>
        </div>
      </Link>
    );
  }

 

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15,
        when: 'beforeChildren',
        staggerChildren: 0.1
      }
    }
  };

  const childVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 200 }
    }
  };

  return (
    <motion.div
      className="bg-gradient-to-br from-gray-900/90 to-black/90 rounded-3xl overflow-hidden border border-gray-800/50 shadow-2xl flex flex-col h-full  group relative backdrop-blur-sm"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      whileHover={{ 
        y: -8,
        borderColor: 'rgba(139, 92, 246, 0.3)',
        boxShadow: '0 20px 40px rgba(139, 92, 246, 0.15)'
      }}
      transition={{ type: 'spring', stiffness: 300 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      {/* Glow effect on hover */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-3xl"
        animate={{ opacity: isHovered ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      />

      {/* Animated border */}
      <motion.div
        className="absolute inset-0 rounded-3xl p-[1px]"
        style={{
          background: 'linear-gradient(45deg, #8b5cf6, #ec4899, #3b82f6)',
        }}
        animate={{
          background: [
            'linear-gradient(45deg, #8b5cf6, #ec4899, #3b82f6)',
            'linear-gradient(45deg, #3b82f6, #8b5cf6, #ec4899)',
            'linear-gradient(45deg, #ec4899, #3b82f6, #8b5cf6)',
          ]
        }}
        transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
      >
        <div className="absolute inset-[1px] bg-gray-900 rounded-3xl" />
      </motion.div>

      <div className="relative z-10 p-5 flex flex-col h-full">
        {/* Badge */}
        <motion.div
          variants={childVariants}
          className="self-start mb-4"
        >
          {offer.badge && (
            <div className={`px-3 py-1.5 rounded-full text-xs font-bold text-white ${badgeColor} flex items-center gap-1.5`}>
              <Zap className="w-3 h-3" />
              {offer.badge}
            </div>
          )}
        </motion.div>

        {/* Content */}
        <div className="flex flex-col  gap-5 items-center lg:items-start flex-grow">
          {/* Image container */}
          <motion.div
            variants={childVariants}
            className="relative w-full  rounded-2xl overflow-hidden flex-shrink-0 group/image"
          >
            <motion.img
              src={offer.image}
              alt={offer.title}
              className="w-full h-full object-cover"
              whileHover={{ scale: 1.1 }}
              transition={{ duration: 0.4 }}
            />
            
            {/* Shimmer effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              initial={{ x: '-100%' }}
              animate={{ x: isHovered ? '100%' : '-100%' }}
              transition={{ duration: 0.8 }}
            />
            
            {/* Verified badge */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="absolute -bottom-2 z-30 -right-2 bg-gradient-to-r from-green-500 to-emerald-600 p-1.5 rounded-full"
            >
              <Shield className="w-4 h-4 text-white" />
            </motion.div>
          </motion.div>

          {/* Text content */}
          <div className="flex-grow min-w-0">
            <motion.h3
              variants={childVariants}
              className="font-bold text-base lg:text-lg text-white leading-tight mb-2 line-clamp-2"
            >
              {offer.title}
            </motion.h3>
            
            <motion.p
              variants={childVariants}
              className="text-sm lg:text-[15px] text-gray-300 leading-relaxed mb-4 line-clamp-3"
            >
              {offer.subtitle}
            </motion.p>

            {/* Stats */}
            <motion.div
              variants={childVariants}
              className="flex flex-wrap gap-3 mb-5"
            >
              {offer.duration && (
                <div className="flex items-center gap-1.5 text-xs text-gray-400 bg-gray-800/50 px-2.5 py-1.5 rounded-lg">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="text-[11px] lg:text-[12px]">{offer.duration}</span>
                </div>
              )}
              
              {offer.users && (
                <div className="flex items-center gap-1.5 text-xs text-gray-400 bg-gray-800/50 px-2.5 py-1.5 rounded-lg">
                  <Users className="w-3.5 h-3.5" />
                  <span className="text-[11px] lg:text-[12px]">{offer.users}</span>
                </div>
              )}
              
              {offer.difficulty && (
                <div className="flex items-center gap-1.5 text-xs text-gray-400 bg-gray-800/50 px-2.5 py-1.5 rounded-lg">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span className="text-xs lg:text-[12px]">{offer.difficulty}</span>
                </div>
              )}
            </motion.div>
          </div>
        </div>

        {/* CTA Button */}
        <motion.div
          variants={childVariants}
          className="mt-auto"
        >
          <Link 
            to={offer.route}
            className={`block w-full text-center py-3.5 rounded-2xl font-bold text-sm lg:text-[15px] transition-all duration-300 
              ${offer.color || 'bg-gradient-to-r from-indigo-500 to-purple-600'} 
              text-white shadow-lg relative overflow-hidden group/button`}
            style={{ boxShadow: '0 10px 30px rgba(139, 92, 246, 0.3)' }}
          >
            {/* Button shine effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
              initial={{ x: '-100%' }}
              whileHover={{ x: '100%' }}
              transition={{ duration: 0.6 }}
            />
            
            <motion.span 
              className="relative flex items-center justify-center gap-2 text-xs lg:text-sm"
              whileHover={{ gap: 3 }}
              transition={{ type: 'spring', stiffness: 400 }}
            >
              {offer.ctaLabel}
              <motion.span
                animate={{ x: isHovered ? 5 : 0 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <ChevronRight className="w-4 h-4" />
              </motion.span>
            </motion.span>
            
            {/* External link indicator */}
            <motion.div
              className="absolute top-3 right-3"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: isHovered ? 1 : 0, scale: isHovered ? 1 : 0 }}
              transition={{ type: 'spring' }}
            >
              <ExternalLink className="w-4 h-4" />
            </motion.div>
          </Link>
          
          {/* Additional info */}
          {offer.note && (
            <motion.p
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: isHovered ? 1 : 0, y: isHovered ? 0 : 5 }}
              className="text-[10px] lg:text-[11px] text-gray-500 text-center mt-2"
            >
              {offer.note}
            </motion.p>
          )}
        </motion.div>
      </div>

      {/* Particle effect on hover */}
      <AnimatePresence>
        {isHovered && (
          <>
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-white rounded-full"
                initial={{ 
                  x: '50%', 
                  y: '50%', 
                  scale: 0,
                  opacity: 1 
                }}
                animate={{
                  x: `${Math.random() * 100}%`,
                  y: `${Math.random() * 100}%`,
                  scale: [0, 1, 0],
                  opacity: [0, 1, 0]
                }}
                exit={{ opacity: 0 }}
                transition={{ 
                  duration: 0.8,
                  delay: i * 0.1 
                }}
              />
            ))}
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default OfferCard;